import React, { useEffect, useMemo, useState } from "react";
import {
  getContractReviewStatusPresentation,
  REQUIRED_APPROVAL_CHECKS,
  validateReviewSubmission
} from "./operationPresentation";

const COPY = {
  en: {
    heading: "Review decision",
    approve: "Approve",
    reupload: "Correction required",
    reject: "Reject permanently",
    checklist: "Contract review checklist",
    decisionReason: "Decision reason",
    notes: "Internal review notes",
    confirm: "Confirm decision",
    reasonHint:
      "Write a clear, customer-safe reason that the merchant can act on.",
    reuploadNotice:
      "Only the merchant that owns this operation can upload a corrected signed copy.",
    terminalNotice:
      "This is a terminal decision. It permanently disables contract re-upload and operation activation.",
    terminalConfirmation:
      "I understand that final rejection permanently prevents activation and contract re-upload.",
    latestAttempt: "Latest signed upload",
    viewLatestFirst:
      "View the latest signed contract in Contract files before confirming a decision.",
    readOnly: "This review is complete and is now read-only.",
    reviewer: "Reviewer",
    reviewedAt: "Reviewed at",
    reason: "Decision reason",
    status: "Decision",
    pending: "No completed review decision is available.",
    missingDecision: "Choose a review decision.",
    missingReason: "Enter a decision reason of at least 3 characters.",
    missingChecklist: "Complete every required approval check."
  },
  ar: {
    heading: "قرار مراجعة العقد",
    approve: "اعتماد",
    reupload: "إعادة رفع مطلوبة",
    reject: "رفض نهائي",
    checklist: "قائمة مراجعة العقد",
    decisionReason: "سبب القرار",
    notes: "ملاحظات المراجعة الداخلية",
    confirm: "تأكيد القرار",
    reasonHint: "اكتب سببًا واضحًا وآمنًا للعميل يستطيع التاجر تنفيذه.",
    reuploadNotice:
      "التاجر صاحب هذا الطلب وحده يستطيع رفع نسخة موقعة مصححة.",
    terminalNotice:
      "هذا قرار نهائي يمنع إعادة رفع العقد وتفعيل الطلب بصورة دائمة.",
    terminalConfirmation:
      "أفهم أن الرفض النهائي يمنع التفعيل وإعادة رفع العقد بصورة دائمة.",
    latestAttempt: "أحدث عقد مُوقع",
    viewLatestFirst:
      "اعرض أحدث عقد مُوقع من تبويب ملفات العقد قبل تأكيد القرار.",
    readOnly: "اكتملت هذه المراجعة وأصبحت للقراءة فقط.",
    reviewer: "المراجع",
    reviewedAt: "تاريخ المراجعة",
    reason: "سبب القرار",
    status: "القرار",
    pending: "لا يوجد قرار مراجعة مكتمل حتى الآن.",
    missingDecision: "اختر قرار المراجعة.",
    missingReason: "أدخل سببًا للقرار لا يقل عن 3 أحرف.",
    missingChecklist: "استكمل جميع بنود الاعتماد المطلوبة."
  }
};

const formatReviewedAt = (value, language) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(language === "ar" ? "ar-EG" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: true
  }).format(date);
};

const DecisionOption = ({ value, selected, tone, icon, label, onChange }) => (
  <label className={selected ? `is-selected is-${tone}` : ""}>
    <input
      type="radio"
      name="contract-review-decision"
      value={value}
      checked={selected}
      onChange={() => onChange(value)}
    />
    <i className={`fa-solid ${icon}`} aria-hidden="true" />
    {label}
  </label>
);

const CompletedReview = ({ latestDecision, language, copy }) => {
  if (!latestDecision) {
    return <div className="operation-workflow-notice is-info">{copy.pending}</div>;
  }
  const status = getContractReviewStatusPresentation(
    latestDecision.decision,
    language
  );
  const reason =
    latestDecision.decisionReason || latestDecision.rejectionReason || "—";
  const reviewer =
    latestDecision.reviewer?.name ||
    latestDecision.reviewedBy?.name ||
    latestDecision.reviewerRole ||
    "—";

  return (
    <div className="contract-review-readonly">
      <div className="contract-review-readonly-grid">
        <div>
          <span>{copy.status}</span>
          <strong>{status.label}</strong>
          {!status.known && <code>{status.diagnosticCode}</code>}
        </div>
        <div>
          <span>{copy.reviewer}</span>
          <strong>{reviewer}</strong>
        </div>
        <div>
          <span>{copy.reviewedAt}</span>
          <strong>
            {formatReviewedAt(latestDecision.reviewedAt, language)}
          </strong>
        </div>
        {latestDecision.decision !== "APPROVED" && (
          <div className="is-wide">
            <span>{copy.reason}</span>
            <strong>{reason}</strong>
          </div>
        )}
      </div>
      <div className="operation-workflow-notice is-info">{copy.readOnly}</div>
    </div>
  );
};

const ContractReviewDecisionPanel = ({
  language = "en",
  canReview = false,
  latestAttempt,
  latestDecision,
  reviewedAttemptId,
  checklist = {},
  checklistLabels = {},
  form = {},
  onChecklistChange,
  onFormChange,
  onSubmit,
  actionLoading = "",
  reviewError = ""
}) => {
  const copy = COPY[language] || COPY.en;
  const [terminalConfirmed, setTerminalConfirmed] = useState(false);
  const decision = String(form.decision || "").toUpperCase();
  const latestAttemptId = latestAttempt?.id || "";
  const latestWasViewed =
    Boolean(latestAttemptId) && reviewedAttemptId === latestAttemptId;
  const validation = useMemo(
    () =>
      validateReviewSubmission({
        decision,
        reason: form.decisionReason,
        checklist
      }),
    [checklist, decision, form.decisionReason]
  );

  useEffect(() => setTerminalConfirmed(false), [decision, latestAttemptId]);

  if (!canReview) {
    return (
      <section className="operation-detail-panel contract-review-panel">
        <h6>{copy.heading}</h6>
        <CompletedReview
          latestDecision={latestDecision}
          language={language}
          copy={copy}
        />
      </section>
    );
  }

  const updateForm = (patch) => {
    if (onFormChange) {
      onFormChange((previous) => ({ ...previous, ...patch }));
    }
  };
  const negativeDecision = ["REUPLOAD_REQUIRED", "REJECTED"].includes(
    decision
  );
  const terminalReady = decision !== "REJECTED" || terminalConfirmed;
  const submitting = actionLoading.startsWith("review-");
  const disabled =
    !validation.valid || !latestWasViewed || !terminalReady || submitting;
  const validationMessage =
    decision === ""
      ? copy.missingDecision
      : decision === "APPROVED" && !validation.valid
        ? copy.missingChecklist
        : negativeDecision && !validation.valid
          ? copy.missingReason
          : "";

  const submit = () => {
    if (disabled || !onSubmit) return;
    onSubmit({
      decision,
      relatedUploadAttempt: latestAttemptId,
      decisionReason: negativeDecision
        ? String(form.decisionReason || "").trim()
        : undefined,
      notes: String(form.notes || "").trim() || undefined,
      checklist
    });
  };

  return (
    <section className="operation-detail-panel contract-review-panel">
      <h6>{copy.heading}</h6>
      <div className="contract-review-attempt">
        <i className="fa-solid fa-file-signature" aria-hidden="true" />
        <span>{copy.latestAttempt}</span>
        <strong>#{latestAttempt?.attemptNumber || "—"}</strong>
      </div>

      {!latestWasViewed && (
        <div className="operation-workflow-notice is-warning" role="status">
          {copy.viewLatestFirst}
        </div>
      )}
      {reviewError && (
        <div className="operations-inline-error" role="alert">
          {reviewError}
        </div>
      )}

      <div className="operation-decision-switch is-three-way">
        <DecisionOption
          value="APPROVED"
          selected={decision === "APPROVED"}
          tone="approve"
          icon="fa-circle-check"
          label={copy.approve}
          onChange={(value) =>
            updateForm({ decision: value, decisionReason: "" })
          }
        />
        <DecisionOption
          value="REUPLOAD_REQUIRED"
          selected={decision === "REUPLOAD_REQUIRED"}
          tone="reupload"
          icon="fa-arrow-rotate-right"
          label={copy.reupload}
          onChange={(value) => updateForm({ decision: value })}
        />
        <DecisionOption
          value="REJECTED"
          selected={decision === "REJECTED"}
          tone="reject"
          icon="fa-circle-xmark"
          label={copy.reject}
          onChange={(value) => updateForm({ decision: value })}
        />
      </div>

      {decision === "APPROVED" && (
        <>
          <h6>{copy.checklist}</h6>
          <div className="operations-checklist">
            {Object.entries(checklistLabels).map(([field, label]) => (
              <label className="form-check" key={field}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={checklist[field] === true}
                  onChange={(event) =>
                    onChecklistChange?.(field, event.target.checked)
                  }
                />
                <span className="form-check-label">
                  {label}
                  {REQUIRED_APPROVAL_CHECKS.includes(field) ? " *" : ""}
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      {negativeDecision && (
        <>
          <label className="operation-form-field">
            <span>{copy.decisionReason} *</span>
            <textarea
              rows={3}
              value={form.decisionReason || ""}
              onChange={(event) =>
                updateForm({ decisionReason: event.target.value })
              }
            />
            <small>{copy.reasonHint}</small>
          </label>
          <div
            className={`operation-workflow-notice ${
              decision === "REJECTED" ? "is-danger" : "is-warning"
            }`}
          >
            {decision === "REJECTED"
              ? copy.terminalNotice
              : copy.reuploadNotice}
          </div>
        </>
      )}

      {decision && (
        <label className="operation-form-field">
          <span>{copy.notes}</span>
          <textarea
            rows={2}
            value={form.notes || ""}
            onChange={(event) => updateForm({ notes: event.target.value })}
          />
        </label>
      )}

      {decision === "REJECTED" && (
        <label className="terminal-review-confirmation">
          <input
            type="checkbox"
            checked={terminalConfirmed}
            onChange={(event) => setTerminalConfirmed(event.target.checked)}
          />
          <span>{copy.terminalConfirmation}</span>
        </label>
      )}

      {validationMessage && (
        <p className="contract-review-validation">{validationMessage}</p>
      )}
      <button
        type="button"
        className="btn operation-primary-action"
        disabled={disabled}
        onClick={submit}
      >
        {submitting && (
          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        )}
        {copy.confirm}
      </button>
    </section>
  );
};

export default ContractReviewDecisionPanel;
